
import React, { useRef, useEffect, useState } from 'react';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import { Chart } from 'react-chartjs-2';

import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

/**
 * Create chart gradient
 */
function createGradient(ctx, color) {
  const gradient = ctx.createLinearGradient(0, 0, 0, 250);

  if (color === 'red') {
    gradient.addColorStop(0, 'rgba(255, 0, 0, 0.6)');
    gradient.addColorStop(0.5, 'rgba(255, 128, 128, 0.4)');
    gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
  } else if (color === 'green') {
    gradient.addColorStop(0, 'rgba(0, 128, 0, 0.6)');
    gradient.addColorStop(0.5, 'rgba(144, 238, 144, 0.4)');
    gradient.addColorStop(1, 'rgba(0, 255, 0, 0)');
  } else {
    gradient.addColorStop(0, 'rgba(0, 0, 255, 0.6)');
    gradient.addColorStop(0.5, 'rgba(173, 216, 230, 0.4)');
    gradient.addColorStop(1, 'rgba(0, 0, 255, 0)');
  }

  return gradient;
}

/**
 * Automatically calculate a nice Y-axis range
 */
function getNiceRange(maxValue) {
  if (maxValue <= 0) {
    return {
      max: 100,
      stepSize: 20,
    };
  }

  const rawStep = maxValue / 5;

  const magnitude = Math.pow(
    10,
    Math.floor(Math.log10(rawStep))
  );

  const normalized = rawStep / magnitude;

  let niceNormalized;

  if (normalized <= 1) {
    niceNormalized = 1;
  } else if (normalized <= 2) {
    niceNormalized = 2;
  } else if (normalized <= 5) {
    niceNormalized = 5;
  } else {
    niceNormalized = 10;
  }

  const stepSize = niceNormalized * magnitude;

  return {
    max: stepSize * 5,
    stepSize,
  };
}

function ApexChart({
  color,
  randomNumbers,
  tablename,
  gridLabels,
  showAxis = false,
}) {
  const chartRef = useRef(null);

  /**
   * Chart data
   */
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  /**
   * X-axis range
   *
   * 3    = Last 3 Months
   * 6    = Last 6 Months
   * 12   = Last 12 Months
   * all  = All available data
   */
  const [range, setRange] = useState('12');

  /**
   * Y-axis difference
   *
   * auto = Automatically calculate
   */
  const [yAxisStep, setYAxisStep] = useState('auto');

  /**
   * Filter data according to selected X-axis range
   */
  const getFilteredData = () => {
    const labels = gridLabels || [];
    const numbers = randomNumbers || [];

    if (range === 'all') {
      return {
        labels,
        numbers,
      };
    }

    const count = Number(range);

    return {
      labels: labels.slice(-count),
      numbers: numbers.slice(-count),
    };
  };

  const filteredData = getFilteredData();

  /**
   * Get numeric values
   */
  const numericValues = filteredData.numbers
    .map(Number)
    .filter(Number.isFinite);

  /**
   * Get maximum value from currently
   * selected X-axis range
   */
  const maxDataValue = numericValues.length
    ? Math.max(...numericValues)
    : 0;

  /**
   * Calculate Y-axis range
   *
   * If user selected AUTO:
   *     use getNiceRange()
   *
   * If user selected a specific difference:
   *     use selected difference
   */
  let chartMax;
  let chartStep;

  if (yAxisStep === 'auto') {
    const niceRange = getNiceRange(maxDataValue);

    chartMax = niceRange.max;
    chartStep = niceRange.stepSize;
  } else {
    chartStep = Number(yAxisStep);

    /**
     * Round maximum value UP to the
     * nearest selected Y-axis difference.
     *
     * Example:
     *
     * maxDataValue = 426436
     * yAxisStep    = 100000
     *
     * chartMax = 500000
     */
    if (maxDataValue <= 0) {
      chartMax = chartStep * 5;
    } else {
      chartMax =
        Math.ceil(maxDataValue / chartStep) *
        chartStep;
    }
  }

  /**
   * Create / update chart data
   */
  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) return;

    const data = {
      labels: filteredData.labels,

      datasets: [
        {
          label: tablename,

          data: filteredData.numbers,

          borderColor: color,

          backgroundColor: createGradient(
            chart.ctx,
            color
          ),

          fill: true,

          tension: 0.3,

          borderWidth: 2,

          pointRadius: showAxis ? 3 : 0,

          pointHoverRadius: 5,
        },
      ],
    };

    setChartData(data);
  }, [
    color,
    randomNumbers,
    gridLabels,
    tablename,
    range,
    showAxis,
    yAxisStep,
  ]);

  /**
   * Chart options
   */
  const options = {
    responsive: true,

    maintainAspectRatio: false,

    elements: {
      line: {
        tension: 0.3,
        borderWidth: 1.5,
      },

      point: {
        radius: showAxis ? 3 : 0,
      },
    },

    scales: {
      /**
       * X AXIS
       */
      x: {
        display: showAxis,

        title: {
          display: showAxis,

          text: 'Month',
        },
      },

      /**
       * Y AXIS
       */
      y: {
        display: showAxis,

        beginAtZero: true,

        max: chartMax,

        ticks: {
          stepSize: chartStep,

          callback: function (value) {
            return Number(value).toLocaleString();
          },
        },

        title: {
          display: showAxis,

          text: 'Amount',
        },
      },
    },

    plugins: {
      /**
       * Legend
       */
      legend: {
        display: showAxis,
      },

      /**
       * Tooltip
       */
      tooltip: {
        enabled: true,

        mode: 'index',

        intersect: false,

        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.dataset.label}: ${Number(
              tooltipItem.raw
            ).toLocaleString()}`;
          },
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
      }}
    >

      {/* =========================================
          FILTERS
      ========================================= */}
      {showAxis && (
        <Box
          sx={{
            display: 'flex',

            justifyContent: 'flex-end',

            alignItems: 'center',

            gap: 2,

            mb: 2,

            flexWrap: 'wrap',
          }}
        >

          {/* =====================================
              X-AXIS RANGE
          ===================================== */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
            }}
          >
            <InputLabel>
              Range
            </InputLabel>

            <Select
              value={range}
              label="Range"
              onChange={(e) => {
                setRange(e.target.value);
              }}
            >
              <MenuItem value="3">
                Last 3 Months
              </MenuItem>

              <MenuItem value="6">
                Last 6 Months
              </MenuItem>

              <MenuItem value="12">
                Last 12 Months
              </MenuItem>

              <MenuItem value="all">
                All
              </MenuItem>
            </Select>
          </FormControl>


          {/* =====================================
              Y-AXIS DIFFERENCE
          ===================================== */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
            }}
          >
            <InputLabel>
              Y-Axis Difference
            </InputLabel>

            <Select
              value={yAxisStep}
              label="Y-Axis Difference"
              onChange={(e) => {
                setYAxisStep(e.target.value);
              }}
            >

              {/* Automatic */}
              <MenuItem value="auto">
                Auto
              </MenuItem>

              {/* 10 */}
              <MenuItem value="10">
                10
              </MenuItem>

              {/* 100 */}
              <MenuItem value="100">
                100
              </MenuItem>

              {/* 1,000 */}
              <MenuItem value="1000">
                1,000
              </MenuItem>

              {/* 10,000 */}
              <MenuItem value="10000">
                10,000
              </MenuItem>

              {/* 100,000 */}
              <MenuItem value="100000">
                100,000
              </MenuItem>

              {/* 1,000,000 */}
              <MenuItem value="1000000">
                1,000,000
              </MenuItem>

            </Select>
          </FormControl>

        </Box>
      )}


      {/* =========================================
          CHART
      ========================================= */}
      <div
        style={{
          height: showAxis
            ? '400px'
            : '100px',

          position: 'relative',

          width: '100%',
        }}
      >
        <Chart
          ref={chartRef}
          type="line"
          data={chartData}
          options={options}
        />
      </div>

    </Box>
  );
}

export default ApexChart;
